import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import BulkPassengerUpload from "../components/common/BulkPassengerUpload";
import PageLoader from "../components/common/PageLoader";
import PersonCell from "../components/common/PersonCell";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction } from "../lib/alerts";
import { asArray, unwrapData } from "../lib/catalog";
import { canEditPassengers } from "../lib/bookingStatus";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWritePassengers, roleFromProfile } from "../lib/agencyAccess";
import { validatePassenger } from "../lib/passengerValidation";

const emptyPassenger = {
  uppd_title: "Mr",
  uppd_fname: "",
  uppd_lname: "",
  uppd_gender: "Male",
  uppd_dob: "",
  uppd_passport_no: "",
  uppd_passport_issue_date: "",
  uppd_passport_expiry: "",
  uppd_passport_issue_place: "",
  uppd_adult_type: "adult",
  uppd_image: null,
};

export default function PassengersPage() {
  const [bookings, setBookings] = useState([]);
  const [bookingId, setBookingId] = useState("");
  const [pilgrims, setPilgrims] = useState([]);
  const [form, setForm] = useState(emptyPassenger);
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingPilgrims, setLoadingPilgrims] = useState(false);
  const [agencyRole, setAgencyRole] = useState(null);

  const selectedBooking = bookings.find((booking) => String(booking.booking_id) === String(bookingId));
  const packageId = selectedBooking?.package_id;
  const pilgrimLimit = Number(selectedBooking?.passenger_limit || selectedBooking?.upbu_no_of_passengers || 0);
  const remainingSlots = pilgrimLimit > 0 ? Math.max(pilgrimLimit - pilgrims.length, 0) : 0;
  const allowEdit = agencyRole != null && canWritePassengers(agencyRole) && canEditPassengers(selectedBooking?.booking_status);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [payload, profile] = await Promise.all([
          agentApi.get("/travel-partner/bookings", { params: { limit: 100, page: 1 } }),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setAgencyRole(roleFromProfile(profile));
        const rows = asArray(unwrapData(payload));
        setBookings(rows);
        if (rows[0]?.booking_id) {
          setBookingId(String(rows[0].booking_id));
        }
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_BOOKINGS");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  async function loadPilgrims(nextBookingId = bookingId) {
    if (!nextBookingId) {
      setPilgrims([]);
      return;
    }
    const payload = await agentApi.get(`/travel-partner/bookings/${nextBookingId}/pilgrims`);
    const data = unwrapData(payload);
    setPilgrims(asArray(data.pilgrims || data));
  }

  useEffect(() => {
    if (!bookingId) {
      setPilgrims([]);
      return;
    }
    let alive = true;
    async function run() {
      setLoadingPilgrims(true);
      try {
        await loadPilgrims(bookingId);
      } catch (fetchError) {
        if (alive) {
          setPilgrims([]);
          showApiError(fetchError, "LOAD_PASSENGERS");
        }
      } finally {
        if (alive) setLoadingPilgrims(false);
      }
    }
    run();
    return () => {
      alive = false;
    };
  }, [bookingId]);

  async function addPassenger() {
    if (!allowEdit) return;
    if (!packageId || !bookingId) {
      showErrorAction("PASSENGER_BOOKING_REQUIRED");
      return;
    }
    const validationError = validatePassenger(form);
    if (validationError) {
      showErrorAction(validationError);
      return;
    }
    if (pilgrimLimit > 0 && remainingSlots <= 0) {
      showErrorAction("PASSENGER_LIMIT");
      return;
    }
    if (form.uppd_image) {
      const type = String(form.uppd_image.type || "").toLowerCase();
      const name = String(form.uppd_image.name || "").toLowerCase();
      if (!type.startsWith("image/") && !/\.(jpe?g|png)$/.test(name)) {
        showErrorAction("PASSENGER_PHOTO_TYPE");
        return;
      }
      if (form.uppd_image.size > 5 * 1024 * 1024) {
        showErrorAction("PASSENGER_PHOTO_TOO_LARGE");
        return;
      }
    }
    try {
      setSaving(true);
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === "uppd_image") {
          if (value) formData.append("uppd_image", value);
          return;
        }
        formData.append(key, value == null ? "" : String(value));
      });
      await agentApi.post(`/umrah-packages/${packageId}/bookings/${bookingId}/passengers`, { body: formData });
      setForm(emptyPassenger);
      showSuccessAction("ADD_PASSENGER");
      await loadPilgrims(bookingId);
    } catch (saveError) {
      showApiError(saveError, "ADD_PASSENGER");
    } finally {
      setSaving(false);
    }
  }

  async function removePassenger(passengerId) {
    if (!allowEdit || !packageId || !bookingId || !passengerId) return;
    try {
      setRemovingId(String(passengerId));
      await agentApi.delete(`/umrah-packages/${packageId}/bookings/${bookingId}/passengers/${passengerId}`);
      showSuccessAction("REMOVE_PASSENGER");
      await loadPilgrims(bookingId);
    } catch (removeError) {
      showApiError(removeError, "REMOVE_PASSENGER");
    } finally {
      setRemovingId("");
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Passengers"
          title="Pilgrims by booking"
          items={[DASHBOARD_CRUMB, { label: "Passengers" }]}
          actions={
            <AgentSelect
              className="page-search"
              value={bookingId}
              options={bookings.map((booking) => ({
                value: String(booking.booking_id),
                label: `${booking.booking_reference} — ${booking.customer_name || "Unassigned"}`,
              }))}
              onChange={(value) => setBookingId(value)}
              placeholder="Select booking"
              isClearable
            />
          }
        />

        {loading ? <PageLoader message="Loading bookings..." /> : null}

        {allowEdit ? (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="mb-3">Add passenger</h5>
              <p className="small text-secondary mb-3">First name, last name, passport number, and an expiry at least 6 months away are required.</p>
              <div className="agent-passenger-form">
                <div className="agent-passenger-form__field">
                  <label className="form-label">Title</label>
                  <select className="form-select" value={form.uppd_title} onChange={(event) => setForm({ ...form, uppd_title: event.target.value })}>
                    <option>Mr</option>
                    <option>Mrs</option>
                    <option>Ms</option>
                  </select>
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">First name</label>
                  <input className="form-control" value={form.uppd_fname} onChange={(event) => setForm({ ...form, uppd_fname: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Last name</label>
                  <input className="form-control" value={form.uppd_lname} onChange={(event) => setForm({ ...form, uppd_lname: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Gender</label>
                  <select className="form-select" value={form.uppd_gender} onChange={(event) => setForm({ ...form, uppd_gender: event.target.value })}>
                    <option>Male</option>
                    <option>Female</option>
                  </select>
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Date of birth</label>
                  <input type="date" className="form-control" value={form.uppd_dob} onChange={(event) => setForm({ ...form, uppd_dob: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Passport number</label>
                  <input className="form-control" value={form.uppd_passport_no} onChange={(event) => setForm({ ...form, uppd_passport_no: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Passport expiry</label>
                  <input type="date" className="form-control" value={form.uppd_passport_expiry} onChange={(event) => setForm({ ...form, uppd_passport_expiry: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field">
                  <label className="form-label">Passport issue date</label>
                  <input type="date" className="form-control" value={form.uppd_passport_issue_date} onChange={(event) => setForm({ ...form, uppd_passport_issue_date: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field agent-passenger-form__span-2">
                  <label className="form-label">Place of issue</label>
                  <input className="form-control" value={form.uppd_passport_issue_place} onChange={(event) => setForm({ ...form, uppd_passport_issue_place: event.target.value })} />
                </div>
                <div className="agent-passenger-form__field agent-passenger-form__span-2">
                  <label className="form-label">Photo</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                    className="form-control"
                    onChange={(event) => setForm({ ...form, uppd_image: event.target.files?.[0] || null })}
                  />
                </div>
              </div>
              <div className="agent-passenger-form__actions">
                <button type="button" className="btn btn-primary" disabled={saving || !packageId} onClick={addPassenger}>
                  {saving ? "Saving..." : "Add passenger"}
                </button>
              </div>
              {packageId && bookingId ? (
                <div className="mt-4">
                  <BulkPassengerUpload
                    packageId={packageId}
                    bookingId={bookingId}
                    remainingSlots={remainingSlots}
                    onImported={() => loadPilgrims(bookingId)}
                  />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loadingPilgrims ? (
              <div className="text-center py-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
              </div>
            ) : null}
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Passport</th>
                  <th>Visa</th>
                  <th>Documents</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {pilgrims.length > 0 ? pilgrims.map((row) => (
                  <tr key={row.passenger_id}>
                    <td><PersonCell name={row.passenger_name} /></td>
                    <td>{row.uppd_passport_no || "-"}</td>
                    <td><StatusBadge value={row.visa_status || "draft"} /></td>
                    <td>{row.total_documents ?? 0}</td>
                    <td className="text-end">
                      <div className="d-inline-flex align-items-center justify-content-end gap-2">
                        <Link
                          to={`/bookings/${bookingId}`}
                          className="agent-icon-btn"
                          title="Open booking"
                          aria-label="Open booking"
                        >
                          <iconify-icon icon="solar:eye-outline"></iconify-icon>
                        </Link>
                        {allowEdit ? (
                          <button
                            type="button"
                            className="agent-icon-btn"
                            title="Remove passenger"
                            aria-label="Remove passenger"
                            disabled={removingId === String(row.passenger_id)}
                            onClick={() => removePassenger(row.passenger_id)}
                          >
                            <iconify-icon icon="solar:trash-bin-minimalistic-outline"></iconify-icon>
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan="5" className="text-secondary text-center py-4">{bookingId ? "No pilgrims on this booking." : "Select a booking to view pilgrims."}</td></tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
